import Link from 'next/link';
import { type ComponentPropsWithoutRef, type MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { type Board } from '@air/api/types';
import { ChevronDownIcon, ChevronRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { type DropdownMenuProps } from '@air/primitive-dropdown-menu';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import { type DragType, type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { DNDBoardDrag } from '~/components/v2/components/shared/dnd/DNDBoardDrag';
import { DNDBoardDrop, type DNDBoardDropProps } from '~/components/v2/components/shared/dnd/DNDBoardDrop';
import { WorkspaceSidebarBoardsList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspacesSidebarBoardsListItemActions } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemActions';
import { WorkspaceSidebarBoardsListItemButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemButton';
import { WorkspaceSidebarBoardsListItemContextMenu } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemContextMenu';
import { WorkspaceSidebarBoardsListItemRenameInput } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemRenameInput';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useExpandSidebarBoardOnFirstSubBoard } from '~/components/v2/hooks/useExpandSidebarBoardOnFirstSubBoard';
import { usePrivateBoardLinkClick } from '~/components/v2/hooks/usePrivateBoardLinkClick';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { createBoardRoute } from '~/constants/routes';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { isAncestorOfCentralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canCreateBoard,
  canDeleteBoard,
} from '~/utils/permissions/boardPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE = 24;

const WORKSPACE_SIDEBAR_BOARD_ITEM_GAP = 8;

export type WorkspaceSidebarBoardsListItemProps = ComponentPropsWithoutRef<'div'> &
  Pick<DNDBoardDropProps, 'supportedBoardDragTypes'> & {
    /**
     * If true, the board will automatically expand when the user is
     * in the ancestor of the board.
     */
    autoExpand?: boolean;
    board: Board;
    depth: number;
    /**
     * Drag and drop type that dictates what is draggable onto the board
     */
    dragType: DragType.boardNavWorkspace | DragType.boardNavFavorites | DragType.boardNavLibrary;
    draggable?: boolean;
    droppable?: boolean;
    index: number;
    onBoardRearrange: (props: {
      adjacentItem: RearrangableItem;
      parentBoardId: Board['id'] | null;
      boards: Board[];
      board: GetBoardDragItem;
    }) => void;
  };

/** ui2026 root-row (depth 0) indent, in px. */
const WORKSPACE_SIDEBAR_BOARD_ROOT_ROW_PADDING_LEFT = 10;

export const WorkspaceSidebarBoardsListItem = memo(
  ({
    autoExpand = false,
    board,
    depth,
    draggable = true,
    dragType,
    droppable = true,
    index,
    onBoardRearrange,
    supportedBoardDragTypes,
    ...restOfProps
  }: WorkspaceSidebarBoardsListItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const [isRenaming, setIsRenaming] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: board.id })?.open);
    const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: board.id })?.touched);
    const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
    const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
    const currentBoardId = useURLBoardIdSelector();
    const { boardPermissions } = useBoardPermissions({
      boardId: board.id,
    });
    const isBoardActive = currentBoardId === board.id;
    // UI2026 hides the chevron unless this board has direct children. The legacy
    // sidebar always shows expand so empty boards can still open (create sub-board).
    const shouldShowExpandControl = ui2026 ? (board.directDescendantBoardCount ?? 0) > 0 : true;
    const isExpanded = open && shouldShowExpandControl;
    const {
      data: subBoards = [],
      isInitialLoading,
      isLoadingMore,
      hasMore,
      loadNextPage,
    } = useSubBoardsList({ boardId: board.id, canFetchData: isExpanded, libraryId: board.library?.id });
    const { createSubBoard } = useCreateSubBoard({
      enableTracking: false,
    });
    const { trackSidebarInteraction } = useTrackSidebarInteraction();
    const isAncestorOfCurrentBoard = useAirSelector((state) => isAncestorOfCentralizedBoardSelector(state, board.id));
    const { onBoardLinkClick } = usePrivateBoardLinkClick({ board });
    useExpandSidebarBoardOnFirstSubBoard({ board, enabled: ui2026 });

    const isCreateBoardButtonVisible = useMemo(() => {
      return (
        !ui2026 &&
        canCreateBoard(boardPermissions) &&
        isExpanded &&
        subBoards.length === 0 &&
        !isInitialLoading &&
        !isLoadingMore
      );
    }, [boardPermissions, isExpanded, isInitialLoading, isLoadingMore, subBoards.length, ui2026]);

    useEffect(() => {
      if (touched || !shouldShowExpandControl) {
        return;
      }

      if (isAncestorOfCurrentBoard) {
        setTreeItem({ id: board.id, open: true, touched: false });
      }
    }, [board.id, isAncestorOfCurrentBoard, setTreeItem, shouldShowExpandControl, touched]);

    useFetchObjectsPermissions({
      objects: {
        boardIds: subBoards.map((board) => board.id),
      },
    });

    const onToggleOpen = useCallback(() => {
      toggleTreeItemOpen({ id: board.id });

      trackSidebarInteraction({
        action: open ? 'collapse' : 'expand',
        type: 'board',
      });
    }, [board.id, trackSidebarInteraction, toggleTreeItemOpen, open]);

    const getCanRearrange = useCallback(() => {
      // For subboards, check if user can rearrange within the parent board
      return canDeleteBoard(boardPermissions) || canChangeBoardAssetsOrBoardsPosition(boardPermissions);
    }, [boardPermissions]);

    const onLinkClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onBoardLinkClick(event);
        trackSidebarInteraction({
          action: 'link_click',
          type: 'board',
        });
      },
      [trackSidebarInteraction, onBoardLinkClick],
    );

    const onCreateSubBoard = useCallback(() => {
      createSubBoard({
        selectedParentBoard: board,
        trackLocation: 'side-nav-extension-item-sub-button',
      });
      trackSidebarInteraction({
        action: 'create_board',
        type: 'board',
      });
    }, [board, createSubBoard, trackSidebarInteraction]);

    useEffect(() => {
      if (touched || !shouldShowExpandControl) {
        return;
      }

      if (isAncestorOfCurrentBoard && autoExpand) {
        setTreeItem({ id: board.id, open: true });
      }
    }, [board.id, autoExpand, isAncestorOfCurrentBoard, shouldShowExpandControl, setTreeItem, touched]);

    const { handleOpenChange: handleDropdownLockChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-board-dropdown-${board.id}`,
    });

    const { handleOpenChange: handleRenameLockChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-board-rename-${board.id}`,
    });

    const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
      (open) => {
        setIsDropdownOpen(open);
        handleDropdownLockChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'board',
          });
        }
      },
      [handleDropdownLockChange, trackSidebarInteraction],
    );

    const onRenamingStart = useCallback(() => {
      handleRenameLockChange(true);
      setIsRenaming(true);
    }, [handleRenameLockChange]);

    const onRenamingEnd = useCallback(() => {
      handleRenameLockChange(false);
      setIsRenaming(false);
    }, [handleRenameLockChange]);

    const handleLoadNextPage = useCallback(() => {
      loadNextPage();
    }, [loadNextPage]);

    const expandControl = useMemo(() => {
      if (!shouldShowExpandControl) {
        if (ui2026) {
          return (
            <span
              aria-hidden
              className={cn(
                'flex shrink-0 items-center justify-center ads:text-ads-gray-6 text-gray-6',
                depth === 0 ? 'size-5' : 'size-6',
              )}
              data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_DECORATIVE_CHEVRON"
            >
              <ChevronRightIcon size="sm" />
            </span>
          );
        }

        return <span aria-hidden="true" className="size-6 shrink-0" />;
      }

      if (ui2026) {
        return (
          <WorkspaceSidebarBoardsListItemButton
            className={cn(
              'relative z-40 rounded-8 ads:text-icon-default text-gray-11 ads:hover:bg-hover-subtle hover:bg-gray-3 ads:hover:text-icon-emphasis hover:text-gray-12 active:bg-gray-4 ads:active:bg-hover-subtle disabled:cursor-not-allowed disabled:opacity-50',
              depth === 0 ? 'size-5' : 'size-6',
            )}
            data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_EXPAND_BUTTON"
            onClick={onToggleOpen}
          >
            <ChevronDownIcon
              className="transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
              size="sm"
            />
          </WorkspaceSidebarBoardsListItemButton>
        );
      }

      return (
        <WorkspaceSidebarBoardsListItemButton
          className="relative z-40 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_EXPAND_BUTTON"
          onClick={onToggleOpen}
        >
          <ChevronDownIcon
            className="transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
            size="sm"
          />
        </WorkspaceSidebarBoardsListItemButton>
      );
    }, [depth, onToggleOpen, shouldShowExpandControl, ui2026]);

    const boardRow = useMemo(
      () => (
        <div
          className={cn(
            'group relative flex h-8 cursor-pointer items-center gap-2 rounded-8 px-2 ui2026:py-1 ui2026:pr-1 font-normal ads:text-metadata text-14 text-gray-11 ui2026:text-gray-10 ui2026:leading-[18px] outline-none transition-colors ads:hover:bg-hover-subtle hover:bg-gray-5 ui2026:hover:bg-gray-2 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-gray-5 ads:active:bg-hover-subtle data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:ring-primary data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-inset ads:data-[active=true]:bg-subtle ads:data-[menu-open=true]:bg-subtle data-[active=true]:bg-gray-5 data-[menu-open=true]:bg-gray-5 ui2026:data-[active=true]:bg-gray-3 ui2026:data-[menu-open=true]:bg-gray-3 ui2026:data-[active=true]:font-medium ads:data-[active=true]:text-primary ui2026:data-[active=true]:text-gray-12',
            depth === 0 ? 'ui2026:gap-1.5' : 'ui2026:gap-1',
          )}
          data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM"
          data-title={board.title}
          data-active={isBoardActive}
          data-menu-open={isDropdownOpen || isContextMenuOpen}
          data-open={isExpanded}
          style={{
            paddingLeft:
              depth === 0 && ui2026
                ? WORKSPACE_SIDEBAR_BOARD_ROOT_ROW_PADDING_LEFT
                : `calc(${depth} * var(--workspace-sidebar-board-indent))`,
          }}
          {...restOfProps}
        >
          <Link
            className="absolute inset-0 z-30 size-full"
            data-testid="WORKSPACE_SIDEBAR_BOARD_LINK"
            href={createBoardRoute(board.id, board.title)}
            onClick={onLinkClick}
          >
            <VisuallyHidden>Go to {board.title}</VisuallyHidden>
          </Link>

          {expandControl}

          {isRenaming ? (
            <WorkspaceSidebarBoardsListItemRenameInput
              board={board}
              onCancel={onRenamingEnd}
              onSubmit={onRenamingEnd}
            />
          ) : (
            <span className="grow truncate">{board.title}</span>
          )}
          <WorkspacesSidebarBoardsListItemActions
            board={board}
            isDropdownOpen={isDropdownOpen}
            onDropdownOpenChange={onDropdownOpenChange}
            onRenamingStart={onRenamingStart}
          />
        </div>
      ),
      [
        board,
        depth,
        expandControl,
        isBoardActive,
        isContextMenuOpen,
        isDropdownOpen,
        isExpanded,
        isRenaming,
        onDropdownOpenChange,
        onLinkClick,
        onRenamingEnd,
        onRenamingStart,
        // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
        restOfProps,
        ui2026,
      ],
    );

    const droppableBoardRow = useMemo(
      () => (
        <DNDBoardDrop
          asChild
          dropLocation="board"
          board={board}
          enabled={droppable}
          supportedBoardDragTypes={supportedBoardDragTypes}
        >
          {boardRow}
        </DNDBoardDrop>
      ),
      [board, boardRow, droppable, supportedBoardDragTypes],
    );

    return (
      <DNDBoardDrag
        className="[--workspace-sidebar-board-indent:12px] ui2026:[--workspace-sidebar-board-indent:14px] data-[dnd-dragging=true]:opacity-50 data-[dnd-moving=true]:opacity-50"
        id={board.id}
        item={{
          type: dragType,
          board,
        }}
        index={index}
        enabled={draggable}
      >
        {ui2026 ? (
          <WorkspaceSidebarBoardsListItemContextMenu
            board={board}
            onOpenChange={setIsContextMenuOpen}
            onRenamingStart={onRenamingStart}
          >
            {droppableBoardRow}
          </WorkspaceSidebarBoardsListItemContextMenu>
        ) : (
          droppableBoardRow
        )}

        {subBoards.length > 0 ? (
          <WorkspaceSidebarCollapsible open={isExpanded}>
            <WorkspaceSidebarBoardsList
              boards={subBoards}
              depth={depth + 1}
              dragType={dragType}
              draggable={draggable}
              droppable={droppable}
              /**
               * We can hardcode this permission check because we know the user is inside of a sub-board so
               * it's the parent board's permissions we need to check
               */
              getCanRearrange={getCanRearrange}
              onBoardRearrange={onBoardRearrange}
              parentBoardId={board.id}
              supportedBoardDragTypes={supportedBoardDragTypes}
            />
            {hasMore ? (
              <Button
                appearance="ghost"
                className="justify-start"
                color="gray"
                isLoading={isLoadingMore}
                onClick={handleLoadNextPage}
                size="small"
                style={{
                  marginLeft: `calc(${depth} * var(--workspace-sidebar-board-indent, 12px) + ${WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE}px + ${WORKSPACE_SIDEBAR_BOARD_ITEM_GAP}px)`,
                }}
              >
                Show more
              </Button>
            ) : null}
          </WorkspaceSidebarCollapsible>
        ) : null}
        {isCreateBoardButtonVisible && (
          <Button
            appearance="ghost"
            className="my-1"
            color="gray"
            onClick={onCreateSubBoard}
            size="small"
            style={{
              marginLeft: `calc(${depth} * var(--workspace-sidebar-board-indent, 12px) + ${WORKSPACE_SIDEBAR_BOARD_ITEM_EXPAND_BUTTON_SIZE}px + ${WORKSPACE_SIDEBAR_BOARD_ITEM_GAP}px)`,
            }}
          >
            <span className="truncate">Create new sub-board</span>
          </Button>
        )}
      </DNDBoardDrag>
    );
  },
);

WorkspaceSidebarBoardsListItem.displayName = 'WorkspaceSidebarBoardsListItem';
